/**
 * OfflineIndicator — connectivity + sync status.
 *
 * TWO CURES (BUG-2026-07-14-VC559):
 *
 * 1. IT NO LONGER BLOCKS THE APP. Every alarm surface here used to be
 *    `fixed inset-x-0 top-0 z-50` — a full-width opaque overlay ABOVE the app header
 *    (MobileLayout, z-30) and the visit-report back/close header (z-10). It therefore
 *    physically covered the controls the rep needed, and the offline banner had no
 *    dismiss control at all: "I am online and I have it all the time, I cannot remove
 *    it, it BLOCKS everything." Alarms are now IN-FLOW (the app reserves the space) or
 *    non-occluding corner badges. Nothing in this file may cover an interactive control.
 *
 * 2. OFFLINE IS PROBE-TRUTH, NOT A FLAG. The old `useOnlineStatus` seeded from the OS
 *    link-layer flag and was only ever mutated by the online/offline events. On the
 *    operator's device that flag read false on a LIVE network, so the red bar was
 *    pinned permanently on a working connection. The banner now renders only when a
 *    reachability PROBE has actually failed.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Wifi, WifiOff, RefreshCw, AlertTriangle, ChevronDown, X } from 'lucide-react'
import { cn } from '@appic/ui-components'
import {
  getPendingCount,
  getFailedEntryIds,
  retryFailedEntries,
  onQueueChange,
  ackFailedIds,
  hasUnackedFailure,
  isSustainedUnreachable,
  onSustainedUnreachableChange,
} from '@appic/lib'
import {
  useFinalPinned, useDiscardFinal, DiscardControl, entryLabel, noRetryCopies, noRetryBannerNote, FINALS_UNKNOWN_COPY, RECHECK_LABEL, NO_FINALS,
  type Finals, type DiscardState, type Recheck,
} from './offlineFinalEntries'

/**
 * Offline is asserted ONLY on probe evidence. `unknown` (no probe has completed yet)
 * is NOT offline — with no evidence we stay optimistic, the same posture the write
 * path now takes.
 * And ONE failed probe is not enough for the bar (2026-10-27): it waits for a
 * SUSTAINED verdict — why, see `@appic/lib` offline/reachabilitySustained.ts.
 */
function useIsOffline(): boolean {
  const [sustained, setSustained] = useState<boolean>(() => isSustainedUnreachable())
  useEffect(() => {
    setSustained(isSustainedUnreachable()) // a transition before the effect ran must not be lost
    return onSustainedUnreachableChange(setSustained)
  }, [])
  return sustained
}

function usePendingCount(): number {
  const [pending, setPending] = useState(0)
  useEffect(() => {
    const refresh = () => { void getPendingCount().then(setPending) }
    refresh()
    return onQueueChange(refresh)
  }, [])
  return pending
}

/** The IDs of genuinely-failed entries — identity, so dismissal can be about identity. */
function useFailedIds(): string[] {
  const [ids, setIds] = useState<string[]>([])
  useEffect(() => {
    const refresh = () => { void getFailedEntryIds().then(setIds) }
    refresh()
    return onQueueChange(refresh)
  }, [])
  return ids
}


/**
 * IN-FLOW alarm strip. `relative`, NOT `fixed` — so it participates in layout and the
 * app header sits BELOW it rather than underneath it. This is the whole point: an alarm
 * must never cover the controls a rep would use to act on it.
 */
function AlarmStrip({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="relative z-0 flex w-full items-center justify-center gap-3 bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground"
    >
      {children}
    </div>
  )
}

function OfflineBanner({ unsynced }: { unsynced: number }) {
  return (
    <AlarmStrip>
      <WifiOff className="h-4 w-4 shrink-0" />
      <span>Offline{unsynced > 0 ? ` — ${unsynced} nicht synchronisiert` : ''}</span>
    </AlarmStrip>
  )
}

function FailedBanner({ failed, finals, retryable, retrying, onRetry, onDismiss, finalsUnknown, onRecheck }: {
  failed: number; finals: Finals; retryable: boolean; retrying: boolean; onRetry: () => void; onDismiss: () => void
} & Recheck) {
  return (
    <AlarmStrip>
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>
        {failed} {failed === 1 ? 'Bericht konnte' : 'Berichte konnten'} nicht gesendet werden
        {noRetryBannerNote(finals)}
        {finalsUnknown && ` — ${FINALS_UNKNOWN_COPY}`}
      </span>
      {finalsUnknown && (
        <button type="button" onClick={onRecheck} className="inline-flex min-h-8 items-center rounded-md bg-destructive-foreground/15 px-2.5 py-1 text-xs font-semibold hover:bg-destructive-foreground/25">
          {RECHECK_LABEL}
        </button>
      )}
      {retryable && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="inline-flex min-h-8 items-center gap-1.5 rounded-md bg-destructive-foreground/15 px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-destructive-foreground/25 disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3 w-3', retrying && 'animate-spin')} />
          Erneut versuchen
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Hinweis ausblenden"
        className="inline-flex min-h-8 items-center rounded-md p-1 transition-colors hover:bg-destructive-foreground/25"
      >
        <X className="h-4 w-4" />
      </button>
    </AlarmStrip>
  )
}

/** Non-occluding corner badge — a failure stays VISIBLE without blocking anything. */
function CornerBadge({ tone, children }: { tone: 'danger' | 'warn' | 'ok'; children: ReactNode }) {
  const toneClass =
    tone === 'danger' ? 'bg-destructive text-destructive-foreground'
      : tone === 'warn' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200'
        : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200'
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium shadow-md',
        toneClass,
      )}
    >
      {children}
    </div>
  )
}

/**
 * Non-occluding corner pill for genuinely-failed entries. The count doubles as
 * a disclosure control (BUG-FE-OFFLINE-FAILEDPILL): a bare "N nicht gesendet"
 * was uninspectable, so the rep could not tell WHICH report was stuck. Clicking
 * it reveals the failed-entry ids in a detail list attached above the pill —
 * identity, not just a number.
 */
function FailedPill({ failed, ids, finals, retryable, retrying, onRetry, discards, onDiscard, finalsUnknown, onRecheck }: {
  failed: number; ids: readonly string[]; finals: Finals; retryable: boolean; retrying: boolean; onRetry: () => void
  discards: ReadonlyMap<string, DiscardState>; onDiscard: (id: string) => void
} & Recheck) {
  const [expanded, setExpanded] = useState(false)
  const detailId = 'offline-failed-details'
  return (
    <>
      {expanded && (
        <div
          id={detailId}
          role="region"
          aria-label="Nicht gesendete Berichte"
          data-track="failed-pill-details"
          className="fixed bottom-16 right-4 z-40 max-h-48 w-64 overflow-y-auto rounded-md bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md ring-1 ring-border"
        >
          <p className="mb-1 font-semibold">Nicht gesendete Berichte</p>
          <ul className="space-y-0.5">
            {ids.map((id) => [id, finals.get(id)] as const).map(([id, why]) => (
              <li key={id} className="flex items-center gap-2 font-mono text-muted-foreground">
                <span className="truncate">{id}</span>
                {why !== undefined && <span className="shrink-0">{entryLabel(why)}</span>}
                {why !== undefined && <DiscardControl state={discards.get(id)} onDiscard={() => onDiscard(id)} />}
              </li>
            ))}
          </ul>
          {noRetryCopies(finals).map((copy) => <p key={copy} className="mt-1 font-sans">{copy}</p>)}
          {finalsUnknown && <p className="mt-1 font-sans">{FINALS_UNKNOWN_COPY}</p>}
        </div>
      )}
      <CornerBadge tone="danger">
        <AlertTriangle className="h-3 w-3 shrink-0" />
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={detailId}
          className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 transition-colors hover:bg-destructive-foreground/15"
        >
          <span>{failed} nicht gesendet</span>
          <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
        </button>
        {retryable && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            aria-label="Erneut versuchen"
            className="inline-flex min-h-6 items-center rounded-md bg-destructive-foreground/15 px-1.5 py-0.5 transition-colors hover:bg-destructive-foreground/25 disabled:opacity-50"
          >
            <RefreshCw className={cn('h-3 w-3', retrying && 'animate-spin')} />
          </button>
        )}
        {finalsUnknown && (
          <button type="button" onClick={onRecheck} aria-label={RECHECK_LABEL} className="inline-flex min-h-6 items-center gap-1 rounded-md bg-destructive-foreground/15 px-1.5 py-0.5 hover:bg-destructive-foreground/25">
            <span>Status unbekannt</span>
          </button>
        )}
      </CornerBadge>
    </>
  )
}

function SyncedDot() {
  const [visible, setVisible] = useState(true)
  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 3000)
    return () => clearTimeout(timer)
  }, [])
  if (!visible) return null
  return (
    <CornerBadge tone="ok">
      <Wifi className="h-3 w-3" />
      Synchronisiert
    </CornerBadge>
  )
}

export function OfflineIndicator() {
  const isOffline = useIsOffline()
  const pending = usePendingCount()
  const failedIds = useFailedIds()
  const [retrying, setRetrying] = useState(false)

  // Re-derived from the PERSISTED ack store on every change to the failed SET, so a
  // dismissal survives a cold reload (the old `useState(0)` count forgot on every
  // reload, which is why the operator's × appeared not to work) and a genuinely NEW
  // failure — a new entry id, not a bigger number — still re-raises the loud banner.
  const [unacked, setUnacked] = useState(false)
  useEffect(() => { setUnacked(hasUnackedFailure(failedIds)) }, [failedIds])

  const handleRetry = useCallback(async () => {
    setRetrying(true)
    try {
      await retryFailedEntries()
    } finally {
      setRetrying(false)
    }
  }, [])

  const handleDismiss = useCallback(() => {
    ackFailedIds(failedIds)
    setUnacked(false)
  }, [failedIds])

  const failed = failedIds.length
  // NEVER "Erneut versuchen" for a final-pinned entry: offered only once some failed entry is KNOWN not final —
  // never while the read is in flight, and never when it FAILED (finality unknown fails closed).
  const { read, recheck } = useFinalPinned(failedIds)
  const { discards, discard } = useDiscardFinal()
  const finals: Finals = read.state === 'known' ? read.finals : NO_FINALS
  const retryable = read.state === 'known' && failedIds.some((id) => !read.finals.has(id))
  const recheckProps: Recheck = { finalsUnknown: read.state === 'unknown', onRecheck: recheck }

  // Precedence: offline first (a retry is pointless with no connection — show the
  // total unsynced), then failed (online + needs the rep to act), then pending.
  if (isOffline) return <OfflineBanner unsynced={pending + failed} />
  if (failed > 0) {
    return unacked
      ? <FailedBanner failed={failed} finals={finals} retryable={retryable} retrying={retrying} onRetry={handleRetry} onDismiss={handleDismiss} {...recheckProps} />
      : <FailedPill failed={failed} ids={failedIds} finals={finals} retryable={retryable} retrying={retrying} onRetry={handleRetry} discards={discards} onDiscard={discard} {...recheckProps} />
  }
  if (pending > 0) {
    return (
      <CornerBadge tone="warn">
        <RefreshCw className="h-3 w-3 animate-spin" />
        {pending} wird synchronisiert
      </CornerBadge>
    )
  }
  return <SyncedDot />
}
